/**
 * Team Seats — invite co-organizers (Pro only, owner-only surface).
 *
 * Non-owners still see "Shared with you" chip but not the invite form.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, Mail, CheckCircle2, Clock, Crown } from "lucide-react";
import api from "@/lib/api";

export default function TeamPanel({ eventId, isOwner, ownerPlan, theme, onOpenUpgrade }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [inviting, setInviting] = useState(false);
  const isPro = (ownerPlan || "").toLowerCase() === "pro";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/events/${eventId}/collaborators`);
      setRows(res.data?.collaborators || []);
    } catch {
      /* silent — non-owner may not have access */
    } finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    const value = email.trim().toLowerCase();
    if (!value || !/^\S+@\S+\.\S+$/.test(value)) { toast.error("Please enter a valid email"); return; }
    setInviting(true);
    try {
      const res = await api.post(`/events/${eventId}/collaborators`, { email: value, name: name.trim() });
      if (res.data?.already_invited) toast.info(`${value} was already invited`);
      else toast.success(`Invitation on its way to ${value}`);
      setEmail(""); setName("");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        toast.error(typeof detail === "string" ? detail : "Team seats are a Pro feature");
        onOpenUpgrade?.();
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not send invitation");
      }
    } finally { setInviting(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.email} from this event?`)) return;
    try {
      await api.delete(`/events/${eventId}/collaborators/${row.collab_id}`);
      toast.success("Removed");
      await load();
    } catch { toast.error("Could not remove"); }
  };

  if (!isOwner) {
    // Non-owner (collaborator) surface — just a chip.
    return (
      <div className="mt-8 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 flex items-center gap-3" data-testid="team-panel-collab-notice">
        <div className="h-9 w-9 grid place-items-center rounded-xl" style={{ background: `hsl(${theme.accentSoft})`, color: `hsl(${theme.accent})` }}>
          <Users size={16} />
        </div>
        <div className="text-sm">
          <div className="font-semibold">You&apos;re a co-organizer on this event.</div>
          <div className="text-[hsl(var(--muted-foreground))] text-xs">You can moderate tributes, arrange the film, and send invitations. Only the owner can delete the event.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="team-panel">
      <div className="flex items-center gap-2">
        <Users size={16} className="text-[hsl(var(--muted-foreground))]" />
        <h3 className="font-semibold text-sm">Team seats</h3>
        {!isPro && <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]"><Crown size={10} /> Pro</span>}
      </div>
      <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
        Invite family to help moderate tributes and arrange the film together. Only you can delete the event.
      </p>

      <div className="mt-4 grid sm:grid-cols-[1fr_1fr_auto] gap-2">
        <input
          data-testid="team-invite-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Their email"
          type="email"
          disabled={!isPro || inviting}
          className="h-11 rounded-xl px-3 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] text-sm disabled:opacity-60"
        />
        <input
          data-testid="team-invite-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Their name (optional)"
          disabled={!isPro || inviting}
          className="h-11 rounded-xl px-3 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] text-sm disabled:opacity-60"
        />
        {isPro ? (
          <button data-testid="team-invite-submit" onClick={invite} disabled={inviting || !email.trim()}
            className="h-11 px-4 rounded-xl text-white font-semibold text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
            style={{ background: `hsl(${theme.accent})` }}>
            <UserPlus size={15} /> Invite
          </button>
        ) : (
          <button data-testid="team-upgrade-cta" onClick={onOpenUpgrade}
            className="h-11 px-4 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-sm inline-flex items-center gap-1.5">
            <Crown size={15} /> Upgrade to Pro
          </button>
        )}
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="h-10 rounded-lg bg-[hsl(var(--muted))] animate-pulse" />
        ) : rows.length === 0 ? (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            {isPro ? "No co-organizers yet. Invite someone above." : "Upgrade to Pro to invite up to unlimited co-organizers to help moderate and arrange."}
          </p>
        ) : (
          <ul className="divide-y divide-[hsl(var(--border))]">
            {rows.map((r) => (
              <li key={r.collab_id} data-testid={`team-row-${r.collab_id}`} className="py-2.5 flex items-center gap-3">
                <div className="h-8 w-8 shrink-0 rounded-full grid place-items-center bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">
                  <Mail size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{r.name || r.email}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{r.email}</div>
                </div>
                {r.status === "active" ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--success,140_40%_35%))]"><CheckCircle2 size={12} /> Active</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--muted-foreground))]"><Clock size={12} /> Pending</span>
                )}
                <button data-testid={`team-remove-${r.collab_id}`} onClick={() => remove(r)}
                  className="h-8 w-8 grid place-items-center rounded-lg text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] hover:bg-[hsl(var(--muted))]" aria-label="Remove">
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
